import { Form } from 'react-bootstrap';

export interface BulkSelectableDevice {
  name: string;
  node_id?: number;
  ext_id?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface BulkDeviceMultiSelectProps {
  validDevices: BulkSelectableDevice[];
  selectedDevices: BulkSelectableDevice[];
  onChange: (devices: BulkSelectableDevice[]) => void;
  itemLabel?: string;
}

/**
 * Multi-select checkbox list used for bulk-adding devices (USGS stations, buoys, etc.).
 *
 * `itemLabel` (singular) drives user-facing copy — e.g. "station", "buoy", "device".
 */
export default function BulkDeviceMultiSelect({
  validDevices,
  selectedDevices,
  onChange,
  itemLabel = 'device',
}: BulkDeviceMultiSelectProps) {
  const allSelected =
    validDevices.length > 0 && selectedDevices.length === validDevices.length;

  // Rows come straight from `RI_Device_List` (Stage, sirens, lights, cameras)
  // or from a station feed (USGS, buoys). A list row is unique by its node id;
  // a station by its ext_id; names are the last resort and may repeat.
  const keyOf = (d: BulkSelectableDevice) => d.node_id ?? d.ext_id ?? d.name;

  // Same wording as the single-pick dropdown: "<node_id> <name>" when the row
  // has a node id, so operators can tell two like-named rows apart.
  const labelOf = (d: BulkSelectableDevice) =>
    d.node_id !== undefined && d.node_id !== null ? `${d.node_id} ${d.name}` : d.name;

  const handleToggleAll = () => {
    onChange(allSelected ? [] : [...validDevices]);
  };

  const handleToggle = (device: BulkSelectableDevice) => {
    const key = keyOf(device);
    const isSelected = selectedDevices.some((d) => keyOf(d) === key);
    if (isSelected) {
      onChange(selectedDevices.filter((d) => keyOf(d) !== key));
    } else {
      onChange([...selectedDevices, device]);
    }
  };

  const plural = `${itemLabel}s`;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold small">
          Available {plural.charAt(0).toUpperCase() + plural.slice(1)}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary py-0 px-2 text-white"
          onClick={handleToggleAll}
          disabled={validDevices.length === 0}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <div
        className="border rounded overflow-y-auto customScrollbar-Y"
        style={{ maxHeight: '260px' }}
      >
        {validDevices.length === 0 && (
          <div className="px-3 py-2 text-muted small">No {plural} available</div>
        )}
        {validDevices.map((device, idx) => {
          const key = keyOf(device);
          const isChecked = selectedDevices.some((d) => keyOf(d) === key);
          return (
            <label
              key={key ?? idx}
              className="d-flex align-items-center gap-2 px-3 py-2 border-bottom mb-0"
              style={{ cursor: 'pointer' }}
            >
              <Form.Check
                type="checkbox"
                checked={isChecked}
                onChange={() => handleToggle(device)}
                className="mb-0"
              />
              <span className="small">{labelOf(device)}</span>
            </label>
          );
        })}
      </div>

      <div className="mt-2 small text-muted" style={{ minHeight: '1.25rem' }}>
        {selectedDevices.length > 0
          ? `${selectedDevices.length} ${selectedDevices.length === 1 ? itemLabel : plural} selected`
          : `Select one or more ${plural}`}
      </div>
    </div>
  );
}
